/**
 * SCR-012 — Students list (admin + manager).
 * Admin: branch filter + full CRUD. Manager: branch scoped server-side.
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import { type ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { MoreHorizontal, Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@client/components/data-table';
import { StudentForm, type StudentFormValues } from '@client/components/forms/student-form';
import { isApiError, useBranches } from '@client/lib/queries/use-branches';
import {
  type Student,
  type StudentStatus,
  studentKeys,
  useStudents,
  useCreateStudent,
} from '@client/lib/queries/use-students';
import { useBranchFilter } from '@client/store/branch-filter';
import { api } from '@client/lib/api-client';

export const Route = createFileRoute('/admin/students/')({
  component: StudentsListPage,
});

const STATUS_META: Record<StudentStatus, { label: string; variant: 'success' | 'secondary' | 'info' | 'destructive' }> = {
  active:     { label: 'Đang học',      variant: 'success' },
  inactive:   { label: 'Tạm nghỉ',      variant: 'secondary' },
  graduated:  { label: 'Tốt nghiệp',    variant: 'info' },
  withdrawn:  { label: 'Đã nghỉ',       variant: 'destructive' },
};

function StudentsListPage() {
  const { session } = Route.useRouteContext() as {
    session?: { user?: { role?: string } };
  };
  const role = session?.user?.role ?? '';
  const qc = useQueryClient();
  const { selectedBranchId } = useBranchFilter();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [branchFilter, setBranchFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editStudent, setEditStudent] = useState<Student | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const effectiveBranch =
    role === 'manager'
      ? undefined
      : branchFilter !== 'all'
        ? branchFilter
        : selectedBranchId ?? undefined;

  const params = {
    page,
    pageSize: PAGE_SIZE,
    search: search || undefined,
    branchId: effectiveBranch,
    status: statusFilter !== 'all' ? (statusFilter as StudentStatus) : undefined,
  };

  const { data, isLoading, isError, refetch } = useStudents(params);
  const { data: branchesData } = useBranches({ status: 'active', pageSize: 100 });
  const branches = branchesData?.items ?? [];
  const createStudent = useCreateStudent();

  function openCreate() {
    setEditStudent(null);
    setFormError(null);
    setDialogOpen(true);
  }

  function openEdit(s: Student) {
    setEditStudent(s);
    setFormError(null);
    setDialogOpen(true);
  }

  async function handleSubmit(values: StudentFormValues) {
    setFormError(null);
    setIsSubmitting(true);
    try {
      const payload = {
        ...values,
        gender: values.gender || undefined,
        phone: values.phone || undefined,
        email: values.email || undefined,
        address: values.address || undefined,
        note: values.note || undefined,
        branchId: values.branchId || undefined,
        enrolledAt: values.enrolledAt || undefined,
      };
      if (editStudent) {
        await api.patch(`/api/students/${editStudent.id}`, payload);
        await qc.invalidateQueries({ queryKey: studentKeys.all });
        toast.success('Đã cập nhật hồ sơ học viên.');
      } else {
        await createStudent.mutateAsync(payload);
        toast.success('Đã thêm học viên.');
      }
      setDialogOpen(false);
    } catch (err) {
      if (isApiError(err) && err.status === 409) {
        setFormError('Mã học viên đã tồn tại.');
      } else {
        setFormError(isApiError(err) ? err.message : 'Lỗi máy chủ, thử lại sau.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const columns: ColumnDef<Student>[] = [
    {
      accessorKey: 'studentCode',
      header: 'Mã HV',
      cell: ({ row }) => (
        <Link
          to="/admin/students/$studentId"
          params={{ studentId: row.original.id }}
          className="font-mono text-sm text-primary hover:underline"
        >
          {row.original.studentCode}
        </Link>
      ),
    },
    {
      accessorKey: 'fullName',
      header: 'Họ tên',
      cell: ({ row }) => (
        <Link
          to="/admin/students/$studentId"
          params={{ studentId: row.original.id }}
          className="hover:underline"
        >
          {row.original.fullName}
        </Link>
      ),
    },
    {
      accessorKey: 'dateOfBirth',
      header: 'Ngày sinh',
      cell: ({ row }) =>
        row.original.dateOfBirth
          ? format(new Date(row.original.dateOfBirth), 'dd/MM/yyyy')
          : '—',
    },
    {
      accessorKey: 'branchName',
      header: 'Cơ sở',
      cell: ({ row }) => row.original.branchName ?? '—',
    },
    {
      accessorKey: 'phone',
      header: 'SĐT',
      cell: ({ row }) => row.original.phone ?? '—',
    },
    {
      accessorKey: 'status',
      header: 'Trạng thái',
      cell: ({ row }) => {
        const meta = STATUS_META[row.original.status] ?? STATUS_META['active'];
        return (
          <Badge variant={meta?.variant} className="text-xs">
            {meta?.label}
          </Badge>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link
                to="/admin/students/$studentId"
                params={{ studentId: row.original.id }}
              >
                Xem hồ sơ
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => openEdit(row.original)}>
              <Pencil className="h-4 w-4 mr-2" /> Sửa
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-foreground">Học viên</h1>
        <Button
          className="bg-accent text-accent-foreground hover:bg-accent/90 h-10 self-start sm:self-auto"
          onClick={openCreate}
        >
          <Plus className="h-4 w-4 mr-1.5" /> Thêm học viên
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={data?.items ?? []}
        isLoading={isLoading}
        isError={isError}
        onRetry={() => void refetch()}
        emptyMessage="Chưa có học viên nào."
        searchPlaceholder="Tìm tên/mã học viên..."
        searchValue={search}
        onSearchChange={(v) => { setSearch(v); setPage(1); }}
        pagination={data ? { page, pageSize: PAGE_SIZE, total: data.total } : undefined}
        onPageChange={setPage}
        filterSlot={
          <div className="flex flex-wrap gap-2">
            {role === 'admin' && (
              <Select
                value={branchFilter}
                onValueChange={(v) => { setBranchFilter(v); setPage(1); }}
              >
                <SelectTrigger className="h-10 w-[160px]">
                  <SelectValue placeholder="Cơ sở" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tất cả cơ sở</SelectItem>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select
              value={statusFilter}
              onValueChange={(v) => { setStatusFilter(v); setPage(1); }}
            >
              <SelectTrigger className="h-10 w-[140px]">
                <SelectValue placeholder="Trạng thái" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tất cả</SelectItem>
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editStudent ? 'Sửa hồ sơ học viên' : 'Thêm học viên'}</DialogTitle>
          </DialogHeader>
          {formError && (
            <Alert variant="destructive" className="mt-2">
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}
          <StudentForm
            isEdit={!!editStudent}
            isSubmitting={isSubmitting}
            branches={role === 'admin' ? branches : undefined}
            defaultValues={
              editStudent
                ? {
                    studentCode: editStudent.studentCode,
                    branchId: editStudent.branchId,
                    fullName: editStudent.fullName,
                    dateOfBirth: editStudent.dateOfBirth,
                    gender: editStudent.gender ?? '',
                    phone: editStudent.phone ?? '',
                    email: editStudent.email ?? '',
                    address: editStudent.address ?? '',
                    enrolledAt: editStudent.enrolledAt ?? '',
                    status: editStudent.status,
                    note: editStudent.note ?? '',
                  }
                : undefined
            }
            onSubmit={handleSubmit}
            onCancel={() => setDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
